import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	type FormIndexItem,
	FormCardIndex,
	FormViewMode,
	useFormIndexState,
	useSelectedFormStoreState,
} from '@atlassian/proforma-form-system';
import { useIsJCSProjectType } from '@atlassian/jira-servicedesk-customer-service-common/src/utils/use-is-jcs/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { FormSecurityApiProvider, LiveFormSecurityApi } from '@atlassian/proforma-form-security';
import { fg } from '@atlassian/jira-feature-gating';
import { useDownloadForm } from '../../../controllers/use-download-form/index.tsx';
import { useHandleCancelEditForm } from '../../../controllers/use-handle-cancel-edit-form/index.tsx';
import { useHandleCloseForm } from '../../../controllers/use-handle-close-form/index.tsx';
import { useHandleDeleteForm } from '../../../controllers/use-handle-delete-form/index.tsx';
import { useHandleEditForm } from '../../../controllers/use-handle-edit-form/index.tsx';
import { useHandleSelectForm } from '../../../controllers/use-handle-form-selected/index.tsx';
import { useHandleUpdateFormVisibility } from '../../../controllers/use-handle-update-form-visibility/index.tsx';
import { useHandleReopenFormWithIndexItem } from '../../../controllers/use-reopen-form-with-indexitem/index.tsx';
import { issueViewAttachedFormExperience } from './experiences.tsx';
import { JiraFormCardMenu } from './jira-form-card-menu/index.tsx';
import type { JiraFormCardIndexProps } from './types.tsx';

export const JiraFormCardIndex = observer(
	({
		issueViewStore,
		disableIssueViewFullPageDropzoneCallback,
		proformaUrl,
		projectType,
	}: JiraFormCardIndexProps) => {
		const cloudId = useCloudId();
		const { formViewMode, permissions } = useFormIndexState();
		const handleDeleteForm = useHandleDeleteForm(
			issueViewStore.loadFormStore,
			disableIssueViewFullPageDropzoneCallback,
		);
		const handleDownloadPdf = useDownloadForm(
			proformaUrl,
			issueViewStore.issueId,
			issueViewStore.issueKey,
			'pdf',
		);
		const handleDownloadXlsx = useDownloadForm(
			proformaUrl,
			issueViewStore.issueId,
			issueViewStore.issueKey,
			'xlsx',
		);
		const handleCancelEdit = useHandleCancelEditForm();
		const handleReopenFormWithIndexItem = useHandleReopenFormWithIndexItem(projectType);
		const handleCloseForm = useHandleCloseForm(
			issueViewStore.loadFormStore,
			disableIssueViewFullPageDropzoneCallback,
			projectType,
		);
		const handleEditForm = useHandleEditForm(
			issueViewStore.loadFormStore,
			disableIssueViewFullPageDropzoneCallback,
		);
		const { handleSelectOld, handleSelect } = useHandleSelectForm(
			issueViewStore.loadFormStore,
			disableIssueViewFullPageDropzoneCallback,
		);
		const handleUpdateFormVisibility = useHandleUpdateFormVisibility();

		const isJCS = useIsJCSProjectType(projectType);

		// This is a workaround that forces JiraFormCardIndex to re-render whenever isUploadingMedia is updated.
		// It relies on the observer MobX wrapper picking up isUploadingMedia changes.
		// We need this workaround until we re-write FormStore, QuestionStore and how the attachment uploading state is managed.
		const { selectedFormStore } = useSelectedFormStoreState();
		selectedFormStore?.isUploadingMedia;

		useEffect(() => {
			disableIssueViewFullPageDropzoneCallback(formViewMode === FormViewMode.Edit);
		}, [disableIssueViewFullPageDropzoneCallback, formViewMode]);

		const renderMenuIssueView = useCallback(
			(form: FormIndexItem, formStore?: FormStore) => (
				<JiraFormCardMenu
					form={form}
					formStore={formStore}
					permissions={permissions}
					onDeleteForm={handleDeleteForm}
					onDownloadFormPdf={handleDownloadPdf}
					onDownloadFormXlsx={handleDownloadXlsx}
					onUpdateFormVisibility={handleUpdateFormVisibility}
					onReopenForm={handleReopenFormWithIndexItem}
					onCloseForm={handleCloseForm}
					onEditForm={handleEditForm}
					projectType={projectType}
				/>
			),
			[
				permissions,
				handleDeleteForm,
				handleDownloadPdf,
				handleDownloadXlsx,
				handleUpdateFormVisibility,
				handleReopenFormWithIndexItem,
				handleCloseForm,
				handleEditForm,
				projectType,
			],
		);

		const formSecurityApi = new LiveFormSecurityApi(cloudId, issueViewStore.projectId);

		return fg('granular_permissions_on_forms_ff_ga') ? (
			<FormSecurityApiProvider formSecurityApi={formSecurityApi}>
				<FormCardIndex
					renderFormUfoExperience={issueViewAttachedFormExperience}
					onFormSelected={handleSelect}
					onFormSelectedOld={handleSelectOld}
					linkedFieldNames={selectedFormStore?.questions.reduce((fieldIdToName, question) => {
						if (question.issueFieldData && question.formQuestion.jiraField) {
							fieldIdToName.set(question.formQuestion.jiraField, question.issueFieldData.name);
						}
						return fieldIdToName;
					}, new Map())}
					renderMenu={!isJCS ? renderMenuIssueView : undefined}
					onCancelEdit={handleCancelEdit}
					projectType={projectType}
				/>
			</FormSecurityApiProvider>
		) : (
			<FormCardIndex
				renderFormUfoExperience={issueViewAttachedFormExperience}
				onFormSelected={handleSelect}
				onFormSelectedOld={handleSelectOld}
				linkedFieldNames={selectedFormStore?.questions.reduce((fieldIdToName, question) => {
					if (question.issueFieldData && question.formQuestion.jiraField) {
						fieldIdToName.set(question.formQuestion.jiraField, question.issueFieldData.name);
					}
					return fieldIdToName;
				}, new Map())}
				renderMenu={!isJCS ? renderMenuIssueView : undefined}
				onCancelEdit={handleCancelEdit}
				projectType={projectType}
			/>
		);
	},
);
